import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type NotificationTemplateType = "ticket_reminder" | "standalone";

/**
 * Standalone templates are owned by their own edge function and are ignored by
 * process-scheduled-notifications. Map name -> owning function for UI labelling.
 */
export const STANDALONE_TEMPLATE_OWNERS: Record<string, string> = {
  series_interest_digest: "send-series-interest-digest",
  first_timer_attended_followup: "process-first-timer-followups",
  first_timer_noshow_refund: "process-first-timer-followups",
};

export interface NotificationTemplate {
  id: string;
  name: string;
  subject_template: string;
  body_template: string;
  hours_before: number;
  is_active: boolean;
  template_type: NotificationTemplateType;
  channels: string[];
  whatsapp_message_template: string | null;
  in_app_title_template: string | null;
  in_app_body_template: string | null;
  in_app_action_url: string | null;
  send_to_admins: boolean;
  send_time: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplateInput {
  name: string;
  subject_template: string;
  body_template: string;
  hours_before: number;
  is_active?: boolean;
  template_type?: NotificationTemplateType;
  channels?: string[];
  whatsapp_message_template?: string | null;
  in_app_title_template?: string | null;
  in_app_body_template?: string | null;
  in_app_action_url?: string | null;
  send_to_admins?: boolean;
  send_time?: string | null;
}

export function useNotificationTemplates() {
  const queryClient = useQueryClient();

  const templatesQuery = useQuery({
    queryKey: ["notification-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notification_templates")
        .select("*")
        .order("hours_before", { ascending: false });

      if (error) throw error;
      return data as NotificationTemplate[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateTemplateInput) => {
      const { data, error } = await supabase
        .from("notification_templates")
        .insert(input)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-templates"] });
      toast.success("Template created successfully");
    },
    onError: (error) => {
      toast.error(`Failed to create template: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: { id: string } & Partial<CreateTemplateInput>) => {
      const { data, error } = await supabase
        .from("notification_templates")
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-templates"] });
      toast.success("Template updated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to update template: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notification_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-templates"] });
      toast.success("Template deleted successfully");
    },
    onError: (error) => {
      toast.error(`Failed to delete template: ${error.message}`);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("notification_templates")
        .update({ is_active })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-templates"] });
    },
    onError: (error) => {
      toast.error(`Failed to toggle template: ${error.message}`);
    },
  });

  return {
    templates: templatesQuery.data ?? [],
    isLoading: templatesQuery.isLoading,
    error: templatesQuery.error,
    createTemplate: createMutation.mutate,
    updateTemplate: updateMutation.mutate,
    deleteTemplate: deleteMutation.mutate,
    toggleActive: toggleActiveMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
